import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Download, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getBookCover } from '@/lib/assets';

const mkBooks = [
  { id: 1, title: "Benny's Big Adventure", language: 'EL', publisher: 'Scholastic', price: 12.00 },
  { id: 2, title: 'Kisah Si Kancil', language: 'ML', publisher: 'DBP', price: 10.50 },
  { id: 3, title: 'Little Tamil Tales Vol 2', language: 'TL', publisher: 'Grassroots', price: 11.00 },
  { id: 4, title: 'Xiao Xiong De Meng (小熊的梦)', language: 'CL', publisher: 'EPH', price: 9.90 },
  { id: 5, title: 'The Magic Seed', language: 'EL', publisher: 'MC Edu', price: 13.50 },
  { id: 6, title: 'Colours of My World', language: 'EL', publisher: 'Scholastic', price: 11.80 },
  { id: 7, title: 'Dongwu Pengyou (动物朋友)', language: 'CL', publisher: 'EPH', price: 9.90 },
  { id: 8, title: 'Mari Kita Bermain', language: 'ML', publisher: 'DBP', price: 10.20 },
];

const getLangColor = (lang) => {
  const colors = {
    'EL': 'bg-blue-100 text-blue-700',
    'CL': 'bg-red-100 text-red-700',
    'ML': 'bg-green-100 text-green-700',
    'TL': 'bg-orange-100 text-orange-700',
  };
  return colors[lang] || 'bg-slate-100 text-slate-700';
};

export default function MKBookOrder() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-[#5B3F8A]" />
          My Book Order 2026 — MK @ Bendemeer
        </h1>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm text-blue-800">
            ℹ️ Your annual book collection is curated and procured on your behalf by MOE HQ and Civica. 
            All 20 titles have been selected for children aged 5–6 years and are currently in transit to your school.
          </p>
        </CardContent>
      </Card>

      {/* Order Summary */}
      <div className="flex items-center justify-between bg-[#5B3F8A] text-white rounded-lg p-4">
        <div>
          <span className="text-sm text-white/80">Order:</span>
          <span className="ml-2 font-semibold">MK-BND-2026</span>
        </div>
        <div>
          <span className="text-sm text-white/80">Books:</span>
          <span className="ml-2 font-semibold">20</span>
        </div>
        <div>
          <span className="text-sm text-white/80">Languages:</span>
          <span className="ml-2 font-semibold">4</span>
        </div>
        <div>
          <span className="text-sm text-white/80">Budget:</span>
          <span className="ml-2 font-semibold">$550</span>
        </div>
        <Badge className="bg-white/20 text-white">🚚 IN TRANSIT</Badge>
      </div>

      {/* Book Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {mkBooks.map(book => (
          <Card key={book.id} className="overflow-hidden" data-testid={`mk-book-${book.id}`}>
            <img 
              src={getBookCover(book.title)} 
              alt={book.title}
              className="h-28 w-full object-cover"
            />
            <CardContent className="pt-3">
              <Badge className={`${getLangColor(book.language)} text-xs mb-2`}>{book.language}</Badge>
              <h4 className="font-medium text-sm line-clamp-2">{book.title}</h4>
              <p className="text-xs text-slate-500 mt-1">{book.publisher}</p>
              <p className="text-sm font-semibold mt-2">${book.price.toFixed(2)}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="text-sm text-slate-500 text-center">Showing 8 of 20 books</p>

      {/* Order Totals */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-base">Order Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>English (EL): 7 books</span>
              <span>$87.00</span>
            </div>
            <div className="flex justify-between">
              <span>Chinese (CL): 6 books</span>
              <span>$59.40</span>
            </div>
            <div className="flex justify-between">
              <span>Malay (ML): 4 books</span>
              <span>$41.00</span>
            </div>
            <div className="flex justify-between">
              <span>Tamil (TL): 3 books</span>
              <span>$34.30</span>
            </div>
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between">
                <span>Book cost</span>
                <span>$221.70</span>
              </div>
              <div className="flex justify-between">
                <span>Acquisition + logistics</span>
                <span>$56.30</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>TOTAL</span>
                <span>$278.00</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>Budget allocated</span>
                <span>$550.00</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Download Book List PDF
        </Button>
        <Button asChild className="bg-[#5B3F8A] hover:bg-[#4A3278]">
          <Link to="/mk/delivery-tracking">
            <Truck className="w-4 h-4 mr-2" />
            Track My Delivery
          </Link>
        </Button>
      </div>

      <p className="text-xs text-slate-500 text-center">
        These titles were selected by MOE HQ and procured by Civica. 
        If you have suggestions for future years, please contact your MOE regional officer.
      </p>
    </div>
  );
}
